/**
 * Crew Member designation — TEMPLATE Members as real Crew seats
 *
 * Product model (Run 3 correction):
 * - You designate a Member on **Your crew / Crew Chat**
 * - That Member carries a mandate + bound home Space
 * - Crew Chat already has full Losi tools (CRM, search, Spaces, calendar…)
 * - Bound Space is **home context for tools**, NOT "go open that Space's chat"
 *
 * Growth Space custom_instructions remain a C1 backup / Pulse home.
 * Primary runtime identity is Crew Chat designation.
 *
 * Preserve LosiAnimated. No Muse/Vesper.
 */

import {
  buildDefaultCrewChatRoster,
  buildTemplateLeaderCrewChatRoster,
  type CrewChatComputerMode,
  type CrewChatRoster
} from '@/lib/crew/crew-chat'
import {
  crewMemberTemplateToRosterSeat,
  type CrewMemberTemplateBinding
} from '@/lib/crew/member-template-bind'
import {
  getCrewMemberTemplate,
  listCrewMemberTemplates,
  SALES_AGENT_SECOND_SHIFT_TEMPLATE,
  type CrewMemberTemplate,
  type CrewMemberTemplateId
} from '@/lib/crew/member-templates'
import type { MateId } from '@/lib/crew/mates'
import { resolveMateId } from '@/lib/crew/mates'

/** Designated TEMPLATE Members currently active for this workspace Crew. */
export function listDesignatedCrewMemberTemplates(): CrewMemberTemplate[] {
  // Run 3: Sales Agent is the first designated Member.
  return listCrewMemberTemplates().filter(
    t => t.id === SALES_AGENT_SECOND_SHIFT_TEMPLATE.id
  )
}

export function resolveDesignatedTemplateForMate(
  mateId: string | null | undefined
): CrewMemberTemplate | null {
  const id = resolveMateId(mateId)
  if (!id) return null
  return (
    listDesignatedCrewMemberTemplates().find(t => t.mateId === id) ?? null
  )
}

export function resolveDesignatedTemplateById(
  templateId: string | null | undefined
): CrewMemberTemplate | null {
  const t = getCrewMemberTemplate(templateId)
  if (!t) return null
  return listDesignatedCrewMemberTemplates().some(d => d.id === t.id) ? t : null
}

/**
 * Default Crew Chat roster when Your crew has designated TEMPLATE Members:
 * Sales Agent leads; shape mates support. Falls back to trio if none.
 */
export function buildDesignatedCrewChatRoster(
  computerMode: CrewChatComputerMode = 'shared'
): CrewChatRoster {
  const designated = listDesignatedCrewMemberTemplates()
  const primary = designated[0]
  if (!primary) {
    return buildDefaultCrewChatRoster(computerMode)
  }
  return buildTemplateLeaderCrewChatRoster(
    crewMemberTemplateToRosterSeat(primary),
    computerMode
  )
}

/** Prefer static import path without require — used by composer. */
export function buildSalesAgentCrewChatRoster(
  computerMode: CrewChatComputerMode = 'shared'
): CrewChatRoster {
  return buildTemplateLeaderCrewChatRoster(
    crewMemberTemplateToRosterSeat(SALES_AGENT_SECOND_SHIFT_TEMPLATE),
    computerMode
  )
}

/**
 * Mandate + capabilities block for Crew Chat / active Member.
 * This is what makes designation real — not opening a Space chat surface.
 */
export function buildCrewMemberMandateSystemNote(
  template: CrewMemberTemplate
): string {
  return [
    `CREW MEMBER MANDATE — ${template.displayName} (${template.roleLabel})`,
    `Mate id: ${template.mateId}`,
    `Bound home Space: ${template.defaultSpaceName} (${template.defaultSpaceId})`,
    '',
    'You are this Crew Member inside Crew Chat. You already have full Losi capabilities for this workspace — use them. Do not ask the user to open a Space chat to do your job.',
    '',
    'CAPABILITIES (use directly via tools):',
    '- Nexus CRM: read/write logs, notes, lead status (outbound send = escalate / wait for Ayo)',
    '- Web search / research (prospects, reviews, signals)',
    `- Space tools: tasks, notes, sheets on bound Space ${template.defaultSpaceId} (pass this spaceId)`,
    '- Calendar: read-only availability — never book',
    '',
    'NEVER silently: send SMS/email, book calendar, spend money, contact anyone on Sunday.',
    '',
    'STANDING INSTRUCTIONS:',
    template.standingInstructions.trim()
  ].join('\n')
}

/**
 * Append designated-member mandates onto the multi-mate Crew Chat prompt.
 */
export function enrichCrewChatSystemPromptWithDesignations(
  basePrompt: string,
  roster: CrewChatRoster
): string {
  const blocks: string[] = [basePrompt.trim()]
  const seen = new Set<CrewMemberTemplateId>()

  for (const seat of roster.seats) {
    // Owner-written mandate on the seat already lives in the base prompt —
    // skip the TEMPLATE standing-instructions block so custom bonding wins.
    if (typeof seat.mandate === 'string' && seat.mandate.trim()) {
      continue
    }
    const template = resolveDesignatedTemplateForMate(seat.mateId)
    if (!template || seen.has(template.id)) continue
    seen.add(template.id)
    blocks.push(
      '',
      '---',
      buildCrewMemberMandateSystemNote(template)
    )
  }

  blocks.push(
    '',
    '---',
    'CREW CAPABILITY RULE: Designated Members act through Losi tools in this thread. Bound Spaces are tool scope (spaceId), not a requirement to leave Crew Chat.'
  )

  return blocks.join('\n')
}

export function designatedMemberBoundSpaceId(
  crewMemberId: string | null | undefined
): string | null {
  return resolveDesignatedTemplateForMate(crewMemberId)?.defaultSpaceId ?? null
}

export function designatedMemberDisplayName(
  mateId: MateId | string | null | undefined
): string | null {
  return resolveDesignatedTemplateForMate(mateId)?.displayName ?? null
}

/** Binding snapshot for docs / prefs chrome (honest TEMPLATE_BOUND). */
export function salesAgentDesignationBinding(
  boundAt = new Date().toISOString()
): CrewMemberTemplateBinding {
  const t = SALES_AGENT_SECOND_SHIFT_TEMPLATE
  return {
    status: 'TEMPLATE_BOUND',
    templateId: t.id,
    mateId: t.mateId,
    displayName: t.displayName,
    spaceId: t.defaultSpaceId,
    boundAt,
    toolPolicy: t.toolPolicy
  }
}
